import { CalendarClock, Clock3, DatabaseBackup, HardDrive, History, RefreshCw, ShieldCheck } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { platformApi } from '../api'
import { formatBackupAge } from '../backup-format'
import type { BackupObservabilitySummary, BackupPageResult, BackupRun, RestoreDrill } from '../backup-types'
import { Button, Card, Empty, LoadError, PageHead, Pagination, Skeleton, Status, errorMessage, formatDateTime, formatNumber } from '../ui'
import { AnalyticsKpiCard as KpiCard } from './AnalyticsUi'

function formatBytes(value: number | null | undefined) {
  if (value === null || value === undefined) return '—'
  const gib = value / 1024 ** 3
  if (gib >= 1) return `${new Intl.NumberFormat('sr-Latn-RS', { maximumFractionDigits: 2 }).format(gib)} GiB`
  return `${new Intl.NumberFormat('sr-Latn-RS', { maximumFractionDigits: 1 }).format(value / 1024 ** 2)} MiB`
}

function formatSeconds(value: number | null | undefined) {
  if (value === null || value === undefined) return '—'
  if (value < 60) return `${new Intl.NumberFormat('sr-Latn-RS', { maximumFractionDigits: 1 }).format(value)} sek`
  const minutes = Math.floor(value / 60)
  const seconds = Math.round(value % 60)
  return `${minutes} min ${seconds} sek`
}

function LocalTime({ value }: { value?: string | null }) {
  if (!value) return <>—</>
  const utc = new Intl.DateTimeFormat('sr-Latn-RS', { dateStyle: 'medium', timeStyle: 'long', timeZone: 'UTC' }).format(new Date(value))
  return <time dateTime={value} title={`UTC: ${utc}`}>{formatDateTime(value)}</time>
}

const statusCopy = {
  healthy: ['Zdravo', 'Posljednji backup i tehnički restore test su unutar očekivanih rokova.'],
  late: ['Kasni', 'Backup ili mjesečni restore test je prekoračio očekivani rok.'],
  failed: ['Neuspješno', 'Zabilježen je eksplicitan kvar ili su propuštena dva očekivana termina.'],
  unknown: ['Nema podataka', 'Pouzdan telemetry događaj još nije primljen.'],
} as const

const failureStageLabels: Record<string, string> = {
  preflight: 'Početna provjera', database_dump: 'Dump baze', database_encrypt: 'Šifrovanje baze',
  asset_archive: 'Arhiviranje asseta', asset_encrypt: 'Šifrovanje asseta', upload: 'R2 upload',
  verification: 'Provjera uploada', retention: 'Retention', inventory: 'Inventar', download: 'Preuzimanje',
  decrypt: 'Dešifrovanje', database_restore: 'Obnova baze', rls_verification: 'RLS provjera',
  asset_verification: 'Provjera asseta', cleanup: 'Čišćenje', unknown: 'Nepoznata faza',
}

function failureStage(value: string | null) { return value ? failureStageLabels[value] ?? 'Nepoznata faza' : '—' }

export function BackupObservabilityPage() {
  const [summary, setSummary] = useState<BackupObservabilitySummary | null>(null)
  const [runs, setRuns] = useState<BackupPageResult<BackupRun> | null>(null)
  const [drills, setDrills] = useState<BackupPageResult<RestoreDrill> | null>(null)
  const [runsPage, setRunsPage] = useState(1)
  const [drillsPage, setDrillsPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const nextSummary = await platformApi.backupSummary()
      setSummary(nextSummary)
      if (!nextSummary.enabled) { setRuns(null); setDrills(null); return }
      const [nextRuns, nextDrills] = await Promise.all([
        platformApi.backupRuns({ page: runsPage, page_size: 20 }),
        platformApi.restoreDrills({ page: drillsPage, page_size: 20 }),
      ])
      setRuns(nextRuns); setDrills(nextDrills)
    } catch (cause) { setError(errorMessage(cause, 'Backup nadzor nije učitan.')) }
    finally { setLoading(false) }
  }, [drillsPage, runsPage])
  useEffect(() => { void load() }, [load])

  if (!summary && loading) return <div className="backup-observability-page">
    <PageHead eyebrow="Operativna sigurnost" title="Backup i oporavak" description="Read-only pregled tehničkog stanja kopija i restore provjera." />
    <div className="executive-kpi-grid">{Array.from({ length: 4 }, (_, index) => <Card className="analytics-kpi" key={index}><Skeleton lines={3} /></Card>)}</div>
  </div>
  if (!summary && error) return <LoadError message={error} retry={() => void load()} />
  if (!summary) return null

  if (!summary.enabled) return <div className="backup-observability-page">
    <PageHead eyebrow="Operativna sigurnost" title="Backup i oporavak" description="Read-only pregled tehničkog stanja kopija i restore provjera." />
    <Card className="backup-disabled"><ShieldCheck aria-hidden="true" /><div><h2>Nadzor još nije uključen</h2><p>Backup proces radi nezavisno, ali tehničke metrike nisu povezane sa superadmin panelom. Uključite produkcijski feature gate nakon podešavanja odvojenih telemetry tokena i Healthchecks provjere.</p></div></Card>
  </div>

  const [statusTitle, statusDescription] = statusCopy[summary.status]
  const lastSuccess = summary.last_successful_backup
  const latestRestore = summary.last_restore_drill
  const totalCipherBytes = (lastSuccess?.database_cipher_bytes ?? 0) + (lastSuccess?.asset_cipher_bytes ?? 0)

  return <div className="backup-observability-page analytics-dashboard">
    <PageHead eyebrow="Operativna sigurnost" title="Backup i oporavak" description="Read-only pregled šifrovanih kopija i tehničkih restore provjera — bez pristupa sadržaju backupa." action={<Button tone="quiet" busy={loading} onClick={() => void load()}><RefreshCw size={16} aria-hidden="true" /> Osvježi</Button>} />
    {error && <div className="error" role="alert">{error}</div>}

    <div className={`backup-health-banner ${summary.status}`} role="status">
      <span className="backup-health-icon"><DatabaseBackup aria-hidden="true" /></span>
      <span><small>Ukupno stanje</small><b>{statusTitle}</b><em>{statusDescription}</em></span>
      <Status value={summary.status} />
    </div>

    <section className="dashboard-section" aria-labelledby="backup-summary-title">
      <div className="dashboard-section-head"><div><span>Stanje</span><h2 id="backup-summary-title">Najvažniji pokazatelji</h2><p>Vrijeme je lokalno; zadržite pokazivač iznad datuma za tačan UTC prikaz.</p></div></div>
      <div className="executive-kpi-grid backup-kpi-grid">
        <KpiCard icon={<DatabaseBackup />} label="Posljednji uspješan backup" value={formatBackupAge(summary.backup_age_seconds)} detail={<><LocalTime value={lastSuccess?.completed_at ?? lastSuccess?.started_at} /> · <Status value={summary.backup_status} /></>} />
        <KpiCard icon={<CalendarClock />} label="Sljedeći očekivani termin" value={<LocalTime value={summary.next_expected_at} />} detail={`Grace period se dodaje prije alarma.`} tone={summary.backup_status === 'late' || summary.backup_status === 'failed' ? 'attention' : 'default'} />
        <KpiCard icon={<HardDrive />} label="Veličina i trajanje" value={formatBytes(totalCipherBytes || null)} detail={`${formatSeconds(lastSuccess?.duration_seconds)} · DB ${formatBytes(lastSuccess?.database_cipher_bytes)} · asseti ${formatBytes(lastSuccess?.asset_cipher_bytes)}`} />
        <KpiCard icon={<Clock3 />} label="Tehnički restore RTO" value={formatSeconds(latestRestore?.duration_seconds)} detail={<><LocalTime value={latestRestore?.completed_at ?? latestRestore?.started_at} /> · <Status value={summary.restore_status} /></>} tone={summary.restore_status === 'late' || summary.restore_status === 'failed' ? 'attention' : 'default'} />
      </div>
    </section>

    <section className="dashboard-section" aria-labelledby="retention-title">
      <div className="dashboard-section-head"><div><span>Retention</span><h2 id="retention-title">Dostupne kompletne kopije</h2><p>Broje se samo parovi koji imaju i šifrovani database dump i odgovarajući asset arhiv.</p></div></div>
      <div className="retention-grid">
        {([
          ['intraday', 'Tokom dana', 'Nije uključeno u trenutnom rasporedu.'],
          ['daily', 'Dnevne', 'Operativne kopije za posljednje dane.'],
          ['weekly', 'Sedmične', 'Dugoročnija sedmična istorija.'],
          ['monthly', 'Mjesečne', 'Mjesečne arhivske tačke.'],
        ] as const).map(([key, label, description]) => <Card key={key} className="retention-card"><History aria-hidden="true" /><span><small>{label}</small><b>{formatNumber(summary.inventory[key])}</b><em>{description}</em></span></Card>)}
      </div>
    </section>

    <section className="dashboard-section backup-history-grid" aria-label="Istorija backup i restore događaja">
      <Card className="backup-history-card">
        <div className="card-head"><div><h2>Backup istorija</h2><p>Zakazani, ručni i startup-observed događaji.</p></div><Status value={summary.backup_status} /></div>
        {runs?.data.length ? <div className="backup-table-scroll" tabIndex={0}><table className="backup-table"><thead><tr><th>Početak</th><th>Vrsta</th><th>Status</th><th>Trajanje</th><th>Veličina</th><th>Faza</th></tr></thead><tbody>{runs.data.map((run) => <tr key={run.id}><td><LocalTime value={run.started_at} /></td><td><Status value={run.run_kind} /></td><td><Status value={run.status} /></td><td>{formatSeconds(run.duration_seconds)}</td><td>{formatBytes((run.database_cipher_bytes ?? 0) + (run.asset_cipher_bytes ?? 0) || null)}</td><td>{failureStage(run.failure_stage)}</td></tr>)}</tbody></table></div> : loading ? <Skeleton lines={5} /> : <Empty>Još nema backup telemetry događaja.</Empty>}
        <Pagination meta={runs?.meta} onPage={setRunsPage} />
      </Card>

      <Card className="backup-history-card">
        <div className="card-head"><div><h2>Restore testovi</h2><p>Tehničke off-VPS provjere dešifrovanja, RLS-a i asseta.</p></div><Status value={summary.restore_status} /></div>
        {drills?.data.length ? <div className="backup-table-scroll" tabIndex={0}><table className="backup-table"><thead><tr><th>Početak</th><th>Status</th><th>RTO</th><th>RLS tabele</th><th>Asseti</th><th>Faza</th></tr></thead><tbody>{drills.data.map((drill) => <tr key={drill.id}><td><LocalTime value={drill.started_at} /></td><td><Status value={drill.status} /></td><td>{formatSeconds(drill.duration_seconds)}</td><td>{formatNumber(drill.rls_table_count)}</td><td>{formatNumber(drill.asset_file_count)}</td><td>{failureStage(drill.failure_stage)}</td></tr>)}</tbody></table></div> : loading ? <Skeleton lines={5} /> : <Empty>Restore test još nije prijavljen.</Empty>}
        <Pagination meta={drills?.meta} onPage={setDrillsPage} />
      </Card>
    </section>

    <div className="backup-readonly-note"><ShieldCheck size={17} aria-hidden="true" /><span><b>Ovaj ekran je isključivo za nadzor.</b> Restore, brisanje kopija i promjena retentiona ostaju kontrolisane CLI procedure.</span></div>
  </div>
}
